import styles from "./footer.module.less";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { TLink } from "../../../types/link-types";
import Heading from "../../elements/heading/heading";
import LinkWrapper from "../../elements/link-wrapper/link-wrapper";

export type TFooterProps = {
  logoUrl: string;
  headline?: string;
  categories?: Array<TFooterCategory>;
  importantLinks?: Array<TLink>;
} & TSocialMediaIconsProps;

export type TFooterRow = {
  title?: string;
  link?: TLink;
};

export type TFooterSection = {
  title?: string;
  rows: Array<TFooterRow>;
};

export type TFooterCategory = {
  title: string;
  sections: Array<TFooterSection>;
};

export type TSocialMediaIconsProps = {
  facebookUrl?: string;
  instagramUrl?: string;
  linkedinUrl?: string;
  twitterUrl?: string;
  youtubeUrl?: string;
};

const SocialMediaIcons = ({
  facebookUrl,
  instagramUrl,
  linkedinUrl,
  twitterUrl,
  youtubeUrl,
}: TSocialMediaIconsProps) => {
  const { t } = useTranslation();
  return (
    <div className={styles.socialMediaWrapper}>
      {facebookUrl && (
        <a
          key={"facebook"}
          href={facebookUrl}
          className={styles.socialMediaLink}
          aria-label={t("FOOTER_VISIT_FACEBOOK")}
        >
          <span className={["svg-icon-facebook", styles.socialMediaIcon].join(" ")} />
        </a>
      )}
      {twitterUrl && (
        <a key={"twitter"} href={twitterUrl} className={styles.socialMediaLink} aria-label={t("FOOTER_VISIT_X")}>
          <span className={["svg-icon-twitter", styles.socialMediaIcon].join(" ")} />
        </a>
      )}
      {instagramUrl && (
        <a
          key={"instagram"}
          href={instagramUrl}
          className={styles.socialMediaLink}
          aria-label={t("FOOTER_VISIT_INSTAGRAM")}
        >
          <span className={["svg-icon-instagram", styles.socialMediaIcon].join(" ")} />
        </a>
      )}
      {youtubeUrl && (
        <a key={"youtube"} href={youtubeUrl} className={styles.socialMediaLink} aria-label={t("FOOTER_VISIT_YOUTUBE")}>
          <span className={["svg-icon-youtube", styles.socialMediaIcon].join(" ")} />
        </a>
      )}
      {linkedinUrl && (
        <a
          key={"linkedin"}
          href={linkedinUrl}
          className={styles.socialMediaLink}
          aria-label={t("FOOTER_VISIT_LINKEDIN")}
        >
          <span className={["svg-icon-linkedin", styles.socialMediaIcon].join(" ")} />
        </a>
      )}
    </div>
  );
};

const Category = ({ title, sections }: TFooterCategory) => {
  return (
    <div key={title} className={styles.category}>
      <Heading visualType={"h5"} semanticType={"h3"} className={[styles.categoryTitle].join(" ")}>
        {title}
      </Heading>
      <ul className={styles.categorySections}>
        {sections?.map((section) => (
          <li key={section.title} className={styles.categorySectionItem}>
            {section.title && <p className={["body-base bold", styles.sectionTitle].join(" ")}>{section.title}</p>}
            <ul className={styles.categorySectionRows}>
              {section.rows?.map((row, index) => (
                <li key={row.title + "-" + index} className={styles.categorySectionRowItem}>
                  {row.link?.url ? (
                    <LinkWrapper
                      className={["body-s", styles.categorySectionRowLink].join(" ")}
                      url={row.link.url}
                      target={row.link.target}
                      onPreNavigate={row.link.onPreNavigate}
                    >
                      {row.link.title}
                    </LinkWrapper>
                  ) : (
                    <p className={["body-s", styles.categorySectionRow].join(" ")}>{row.title}</p>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Footer = ({
  logoUrl,
  headline,
  categories,
  facebookUrl,
  instagramUrl,
  linkedinUrl,
  twitterUrl,
  youtubeUrl,
  importantLinks,
}: TFooterProps) => {
  const { t } = useTranslation();

  return (
    <footer className={styles.footer} aria-label={t("FOOTER_ARIA_LABEL")}>
      <div className={[styles.topSection, "max-width padded-sides"].join(" ")}>
        <div className={styles.logoTitleWrapper}>
          {logoUrl && <img src={logoUrl} alt="Preem logo" className={styles.logo} />}
          {headline && (
            <Heading visualType={"h2"} semanticType={"h2"} className={styles.headline}>
              {headline}
            </Heading>
          )}
        </div>
        <div className={styles.categoriesWrapper}>
          {categories?.map((category, index) => <Category key={category.title + "-" + index} {...category} />)}
        </div>
        <SocialMediaIcons
          facebookUrl={facebookUrl}
          instagramUrl={instagramUrl}
          linkedinUrl={linkedinUrl}
          twitterUrl={twitterUrl}
          youtubeUrl={youtubeUrl}
        />
      </div>
      <div className={styles.bottomSection}>
        <div className={[styles.bottomSectionInnerWrapper, "max-width padded-sides"].join(" ")}>
          <ul className={styles.importantLinksList}>
            <li
              key={"copyright"}
              className={[styles.importantLink, !importantLinks?.length && styles.noBorder, "body-s"].join(" ")}
            >
              {`Copyright © ${dayjs().format("YYYY")} Preem AB`}
            </li>
            {importantLinks &&
              importantLinks?.map((link, index) => (
                <li key={link.url + "-" + index}>
                  <LinkWrapper
                    url={link?.url}
                    className={[styles.importantLink, "body-s"].join(" ")}
                    target={link?.target}
                    onPreNavigate={link?.onPreNavigate}
                  >
                    {link.title}
                  </LinkWrapper>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
